import { useContext } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageHero from "../components/PageHero";
import { LanguageContext } from "../App";
import { IMAGES } from "../translations";

/* ─── Immagini per capitolo ──────────────────────────────────── */
const CHAPTERS = {
  stay: [
    // Design e identità
    "https://customer-assets.emergentagent.com/job_aurora-duino-minimal/artifacts/ddyuzm0i_WhatsApp%20Image%202022-05-24%20at%2007.47.04.jpeg",
    "https://customer-assets.emergentagent.com/job_aurora-duino-minimal/artifacts/n8f9r0u2_WhatsApp%20Image%202025-04-07%20at%2021.46.05%20%281%29.jpeg",
    // Smart comfort
    "https://customer-assets.emergentagent.com/job_aurora-duino-minimal/artifacts/y0r6vet1_image.png",
    "https://customer-assets.emergentagent.com/job_aurora-duino-minimal/artifacts/ule7uxds_WhatsApp%20Image%202025-04-07%20at%2021.46.05%20%282%29.jpeg",
    // Ingresso & reception
    "https://customer-assets.emergentagent.com/job_aurora-duino-minimal/artifacts/jy9ohttu_WhatsApp%20Image%202025-04-07%20at%2021.48.06%20%282%29.jpeg",
  ],
  coast: [
    "https://customer-assets.emergentagent.com/job_aurora-duino-minimal/artifacts/0930qpn2_Castello-di-Duino_Veduta-aerea-Est-min-2.jpg",
    "https://customer-assets.emergentagent.com/job_aurora-duino-minimal/artifacts/jpw3y9cq_baia%20sistiana.jpg",
    "https://customer-assets.emergentagent.com/job_aurora-duino-minimal/artifacts/w269h82q_milani_c1-162-2735.jpg",
  ],
  mornings: [
    "https://customer-assets.emergentagent.com/job_aurora-duino-minimal/artifacts/t11srkrc_WhatsApp%20Image%202026-05-02%20at%2021.00.11%20%283%29.jpeg",
    "https://customer-assets.emergentagent.com/job_aurora-duino-minimal/artifacts/7lewrmfp_WhatsApp%20Image%202026-05-02%20at%2021.00.11.jpeg",
    "https://customer-assets.emergentagent.com/job_aurora-duino-minimal/artifacts/ce0kf81f_WhatsApp%20Image%202026-05-02%20at%2021.00.11%20%288%29.jpeg",
    "https://customer-assets.emergentagent.com/job_aurora-duino-minimal/artifacts/tqwht5yx_WhatsApp%20Image%202026-05-02%20at%2021.00.11%20%285%29.jpeg",
  ],
  explore: [
    "https://customer-assets.emergentagent.com/job_aurora-duino-minimal/artifacts/twf5hprg_sup.jpg",
    "https://customer-assets.emergentagent.com/job_aurora-duino-minimal/artifacts/h6wi26yv_delfino-verde-gt.jpg",
    "https://customer-assets.emergentagent.com/job_aurora-duino-minimal/artifacts/60owo2o9_torri-slivia-explorerfvg-11.webp",
  ],
};

/* ─── Componente Microcopy divider ───────────────────────────── */
function MicroDivider({ quote }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9 }}
      className="py-16 md:py-20 text-center max-w-xl mx-auto px-6"
    >
      <div className="w-8 h-px bg-[#7B907F] mx-auto mb-6" />
      <p className="font-['Cormorant_Garamond',serif] text-2xl md:text-3xl font-light italic text-[#5C5754]">
        "{quote}"
      </p>
    </motion.div>
  );
}

/* ─── Componente Chapter header ──────────────────────────────── */
function ChapterLabel({ label }) {
  return (
    <motion.p
      initial={{ opacity: 0, x: -10 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="font-['Manrope',sans-serif] text-xs uppercase tracking-[0.4em] text-[#7B907F] mb-6"
    >
      — {label}
    </motion.p>
  );
}

export default function GalleryPage() {
  const { t } = useContext(LanguageContext);
  const g = t.gallery;

  return (
    <>
      {/* ─── HERO ──────────────────────────────────── */}
      <PageHero
        image={IMAGES.gallery_hero}
        overline={g.overline}
        title={g.headline}
        subtitle={g.hero_subtitle}
      />

      {/* ─── OPENING QUOTE (dark, cinematica) ─────── */}
      <section className="bg-[#2A2726] py-16 md:py-20 px-6 text-center">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="font-['Cormorant_Garamond',serif] text-2xl md:text-3xl font-light italic text-[#FDFBF7]/70 max-w-2xl mx-auto leading-relaxed"
        >
          "{g.opening_quote}"
        </motion.p>
      </section>

      {/* ─── CAPITOLO: STAY ───────────────────────── */}
      <section className="bg-[#FDFBF7] pt-20 pb-10 px-6">
        <div className="max-w-7xl mx-auto">
          <ChapterLabel label={g.chapters.stay} />
          {/* Grid asimmetrica: 2 grandi sopra + 3 sotto */}
          <div className="flex flex-col gap-3 md:gap-4">
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              {[CHAPTERS.stay[0], CHAPTERS.stay[1]].map((src, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, delay: i * 0.12 }} className="overflow-hidden">
                  <img src={src} alt="Stay" loading="lazy" className="w-full aspect-[4/3] object-cover hover:scale-105 transition-transform duration-700" />
                </motion.div>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
              {[CHAPTERS.stay[2], CHAPTERS.stay[3], CHAPTERS.stay[4]].map((src, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, delay: i * 0.1 }} className={`overflow-hidden ${i === 1 ? "sm:mt-6 md:mt-10" : ""}`}>
                  <img src={src} alt="Stay detail" loading="lazy" className="w-full aspect-square object-cover hover:scale-105 transition-transform duration-700" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <MicroDivider quote={g.microcopy[0]} />

      {/* ─── CAPITOLO: COAST ──────────────────────── */}
      <section className="bg-[#EBE5DD] pt-10 pb-10 px-6">
        <div className="max-w-7xl mx-auto">
          <ChapterLabel label={g.chapters.coast} />
          {/* Full-width + 2 sotto */}
          <div className="flex flex-col gap-3 md:gap-4">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 1 }} className="overflow-hidden">
              <img src={CHAPTERS.coast[0]} alt="Coast" loading="lazy" className="w-full aspect-[4/3] sm:aspect-[3/2] md:aspect-[16/9] lg:aspect-[16/7] object-cover hover:scale-105 transition-transform duration-700" />
            </motion.div>
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              {[CHAPTERS.coast[1], CHAPTERS.coast[2]].map((src, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, delay: i * 0.12 }} className="overflow-hidden">
                  <img src={src} alt="Coast detail" loading="lazy" className="w-full aspect-[4/3] object-cover hover:scale-105 transition-transform duration-700" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <MicroDivider quote={g.microcopy[2]} />

      {/* ─── CAPITOLO: SLOW MORNINGS ──────────────── */}
      <section className="bg-[#FDFBF7] pt-10 pb-10 px-6">
        <div className="max-w-7xl mx-auto">
          <ChapterLabel label={g.chapters.mornings} />
          {/* 1 grande sinistra + 3 destra */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 1 }} className="col-span-1 md:col-span-2 overflow-hidden">
              <img src={CHAPTERS.mornings[0]} alt="Slow mornings" loading="lazy" className="w-full aspect-[4/3] object-cover hover:scale-105 transition-transform duration-700" />
            </motion.div>
            <div className="flex flex-col gap-3 md:gap-4">
              {[CHAPTERS.mornings[1], CHAPTERS.mornings[2]].map((src, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: i * 0.1 }} className="overflow-hidden flex-1">
                  <img src={src} alt="Morning detail" loading="lazy" className="w-full h-full object-cover aspect-square hover:scale-105 transition-transform duration-700" />
                </motion.div>
              ))}
            </div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, delay: 0.2 }} className="col-span-2 md:col-span-3 overflow-hidden">
              <img src={CHAPTERS.mornings[3]} alt="Morning fresh" loading="lazy" className="w-full aspect-[4/3] sm:aspect-[16/6] md:aspect-[21/6] object-cover object-center hover:scale-105 transition-transform duration-700" />
            </motion.div>
          </div>
        </div>
      </section>

      <MicroDivider quote={g.microcopy[1]} />

      {/* ─── CAPITOLO: EXPLORE ────────────────────── */}
      <section className="bg-[#EBE5DD] pt-10 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <ChapterLabel label={g.chapters.explore} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 1 }} className="md:col-span-2 overflow-hidden">
              <img src={CHAPTERS.explore[0]} alt="Explore" loading="lazy" className="w-full aspect-[4/3] object-cover hover:scale-105 transition-transform duration-700" />
            </motion.div>
            <div className="flex flex-col gap-3 md:gap-4">
              {[CHAPTERS.explore[1], CHAPTERS.explore[2]].map((src, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: i * 0.12 }} className="overflow-hidden flex-1">
                  <img src={src} alt="Explore" loading="lazy" className="w-full h-full object-cover aspect-square hover:scale-105 transition-transform duration-700" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ──────────────────────────────── */}
      <section className="bg-[#2A2726] py-20 md:py-28 px-6 text-center" data-testid="gallery-cta-section">
        <div className="max-w-xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.7 }}
            className="font-['Manrope',sans-serif] text-xs uppercase tracking-[0.3em] text-[#7B907F] mb-6"
          >
            {g.overline}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.9, delay: 0.1 }}
            className="font-['Cormorant_Garamond',serif] text-3xl md:text-4xl md:text-5xl font-light text-[#FDFBF7] tracking-tight mb-5"
          >
            {g.final_line}
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.3 }}
          >
            <Link
              to="/prenota"
              data-testid="gallery-page-book-btn"
              className="inline-flex items-center justify-center bg-[#7B907F] text-white font-['Manrope',sans-serif] text-xs uppercase tracking-[0.25em] w-full sm:w-auto px-12 py-5 hover:bg-[#687A6C] transition-all duration-300"
            >
              {g.final_cta}
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
